import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './debughelper.css';
import Package from '../../../package.json';
import { ArrayHelpers, SystemText } from '@salesperformanceint/e4shared';
import { loadText } from './../../actions/ui/index.js';

export const DebugHelper = () => {
    const user = useSelector(state => state.user.user);
    const [show, setShow] = React.useState(false);
    const outer = React.useRef();
    const dispatch = useDispatch();
    const [loadingTranslations, setLoadingTranslations] = React.useState(false);
    const { text } = useSelector(state => state.ui);

    const canViewDebugInfo = user?.authority === 'PARTNER' || user?.authority === 'SPOOF';

    if (!canViewDebugInfo) {
        return null;
    }

    const handleKeyPress = useCallback(event => {
        if (event.altKey && event.code === 'KeyI') {
            setShow(true);
        }
    }, []);

    const reloadTranslations = () => {
        setLoadingTranslations(true);
        dispatch(loadText()); // reload the translations from the server
    };

    function formatDebugInfo(obj) {
        let output = '';

        for (const sectionName of Object.keys(obj)) {
            output += sectionName + '\n\n';

            for (const { key, value } of obj[sectionName]) {
                output += `${key}: ${value}\n`;
            }

            output += '\n'; // blank line between sections
        }

        return output.trim();
    }

    const handleCopy = async (debugInfo, externalDebugInfo) => {
        try {
            const allDebugInfo = {
                'User Debug Info:': debugInfo,
                'Component Debug Info:': externalDebugInfo,
            };

            const formattedInfo = formatDebugInfo(allDebugInfo);

            await navigator.clipboard.writeText(formattedInfo);
        } catch (err) {
            console.error('Failed to copy debug info', err);
        }
    };

    useEffect(() => {
        if (!ArrayHelpers.existsAndHasLength(text)) {
            setLoadingTranslations(true);
        } else {
            setLoadingTranslations(false);
        }
    }, [text]);

    useEffect(() => {
        // attach the event listener
        document.addEventListener('keydown', handleKeyPress);

        // remove the event listener
        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [handleKeyPress]);

    React.useEffect(() => {
        const handleClick = e => {
            if (!outer.current?.contains(e.target)) {
                setShow(false);
            }
        };

        document.addEventListener('mousedown', handleClick, false);

        return () => {
            document.removeEventListener('mousedown', handleClick, false);
        };
    }, []);

    const debugInfo = [];

    debugInfo.push({ key: 'user-fullname', value: user.full_Name });
    debugInfo.push({ key: 'user-id', value: user.id });
    debugInfo.push({ key: 'user-posthogid', value: user.unique_Id });
    debugInfo.push({ key: 'user-language', value: user.language_Code });
    debugInfo.push({ key: 'user-authority', value: user.authority });
    debugInfo.push({ key: 'company-name', value: user.companySettings.name });
    debugInfo.push({ key: 'company-id', value: user.company_Id });
    debugInfo.push({ key: 'IsLearn2', value: user?.companySettings?.isLearn2?.toString() });
    debugInfo.push({ key: 'current-url', value: window.location.href });
    debugInfo.push({ key: 'app-version', value: Package.version });
    debugInfo.push({ key: 'browser', value: navigator.userAgent });
    debugInfo.push({ key: 'viewport-size', value: `${window.innerWidth}x${window.innerHeight}` });
    debugInfo.push({ key: 'timestamp', value: new Date().toISOString() });
    debugInfo.push({ key: 'time zone', value: Intl.DateTimeFormat().resolvedOptions().timeZone });

    /**
     * Allow other components to add debug info by dispatching a custom event.
     * Usage: window.dispatchEvent(new CustomEvent('addDebugInfo', { detail: { key: 'foo', value: 'bar' } }))
     */
    const [externalDebugInfo, setExternalDebugInfo] = React.useState([]);

    useEffect(() => {
        const handler = e => {
            setExternalDebugInfo(prev => prev.filter(item => item.key !== e.detail.key));

            if (e.detail.value && e.detail.value.trim().length) {
                setExternalDebugInfo(prev => [...prev, e.detail]);
            }
        };
        window.addEventListener('addDebugInfo', handler);
        return () => window.removeEventListener('addDebugInfo', handler);
    }, []);

    return (
        <div className="aside-debug-info" ref={outer}>
            {show && (
                <div className="debug-info-open">
                    <h3 className="mb10">
                        User Debug Info
                        <button
                            id="copy-info-button"
                            className="btn btn-xs btn-default pull-right"
                            onClick={() => handleCopy(debugInfo, externalDebugInfo)}
                            data-testid="button-copy-info"
                            title={SystemText('General/DebugInfo/Actions/CopyInfo')}
                        >
                            <i className="fa fa-copy" />
                        </button>
                    </h3>

                    <ul>
                        {debugInfo.map(info => (
                            <li key={info.key}>
                                <strong>{info.key}:</strong> {info.value}
                            </li>
                        ))}
                    </ul>
                    {externalDebugInfo.length > 0 && (
                        <>
                            <h3>Component Debug Info</h3>
                            <ul>
                                {externalDebugInfo.map(info => (
                                    <li key={`external-${info.key}`}>
                                        <strong>{info.key}:</strong> {info.value}
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}

                    <div className="visible-xs">Screen-XS</div>
                    <div className="visible-sm">Screen-SM</div>
                    <div className="visible-md">Screen-MD</div>
                    <div className="visible-lg">Screen-LG</div>

                    <div className="mt10">
                        <h3 className="mb10">Actions</h3>
                        <button
                            id="reload-translations-button"
                            className="btn btn-default"
                            onClick={reloadTranslations}
                            disabled={loadingTranslations}
                            data-testid="button-reload-translations"
                        >
                            {loadingTranslations
                                ? 'loading translations...'
                                : SystemText('General/DebugInfo/Actions/ReloadTranslations')}
                        </button>
                        <span className="ml10">{loadingTranslations && <i className="fa fa-spinner fa-spin" />}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DebugHelper;
